/**
 * Admin controls for the team list, tags, the approval queue, and who each discipline emails.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authFetch, authPost } from '../../utils/api';
import TeamNotifications from './TeamNotifications';
import './Support.css';

async function read(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Something went wrong.');
  return data;
}

export default function SupportSettings({ userRole }) {
  const [data, setData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [discipline, setDiscipline] = useState('electrical');

  function load() {
    authFetch('/api/tickets/settings').then(read).then(setData).catch((err) => setError(err.message));
    authFetch('/api/faq').then(read).then((body) => setFaqs(body.items || [])).catch(() => {});
  }

  useEffect(() => { load(); }, []);

  if (userRole && userRole !== 'admin') {
    return (
      <section className="support-page">
        <p>Only an admin can change support settings.</p>
        <Link to="/support">Back to tickets</Link>
      </section>
    );
  }

  async function addTag(event) {
    event.preventDefault();
    setError('');
    try {
      await read(await authPost('/api/tickets/settings/tags', { label, discipline, category: 'component' }));
      setLabel('');
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveRouting(event) {
    event.preventDefault();
    const form = event.target;
    const people = {};
    (data.disciplines || []).forEach((item) => {
      people[item.id] = {
        name: form[`name-${item.id}`].value,
        email: form[`email-${item.id}`].value,
      };
    });
    setError('');
    try {
      await read(await authFetch('/api/tickets/settings/routing', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ people }),
      }));
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeTag(tag) {
    if (!window.confirm(`Delete the ${tag.label} tag? It comes off every ticket. This cannot be undone.`)) return;
    setError('');
    try {
      await read(await authFetch(`/api/tickets/settings/tags/${encodeURIComponent(tag.slug)}`, { method: 'DELETE' }));
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function decide(term, body) {
    setError('');
    try {
      await read(await authPost(`/api/tickets/settings/suggestions/${encodeURIComponent(term)}`, body));
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function publish(id, status) {
    setError('');
    try {
      await read(await authPost(`/api/faq/${encodeURIComponent(id)}`, { status }));
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section className="support-page">
      <div className="support-head">
        <h1>Support settings</h1>
        <Link className="rf-btn rf-btn-outline" to="/support">Back to tickets</Link>
      </div>
      {error && <p className="support-error">{error}</p>}
      {data && (
        <>
          <TeamNotifications team={data.team} emailConfigured={data.emailConfigured} onSaved={load} />

          <form className="support-form rf-card rf-card-body" onSubmit={async (event) => {
            event.preventDefault();
            const form = event.target;
            setError('');
            try {
              await read(await authFetch('/api/tickets/settings/targets', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  targets: {
                    urgent: Number(form.urgent.value),
                    high: Number(form.high.value),
                    normal: Number(form.normal.value),
                    low: Number(form.low.value),
                  },
                }),
              }));
              load();
            } catch (err) {
              setError(err.message);
            }
          }}>
            <h2>First reply targets</h2>
            <p className="support-meta">Clock hours, not working hours. A stopped machine uses the urgent target. A ticket with no first reply after that shows as overdue.</p>
            {[['urgent', 'Urgent'], ['high', 'High'], ['normal', 'Normal'], ['low', 'Low']].map(([key, label]) => (
              <label key={key} className="rf-label" htmlFor={`target-${key}`}>
                {label} (hours)
                <input id={`target-${key}`} name={key} className="rf-input" type="number" min="1" max="336" required defaultValue={data.targets?.[key] ?? ''} />
              </label>
            ))}
            <button type="submit" className="rf-btn rf-btn-primary">Save targets</button>
          </form>

          <form className="support-form rf-card rf-card-body" onSubmit={addTag}>
            <h2>Tags</h2>
            <p className="support-meta">New part numbers wait here until you approve them. Keywords only apply tags on this list.</p>
            <label className="rf-label" htmlFor="tag-label">New tag</label>
            <input id="tag-label" className="rf-input" value={label} onChange={(event) => setLabel(event.target.value)} required />
            <label className="rf-label" htmlFor="tag-discipline">Discipline</label>
            <select id="tag-discipline" className="rf-input" value={discipline} onChange={(event) => setDiscipline(event.target.value)}>
              {(data.disciplines || []).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
            <button type="submit" className="rf-btn rf-btn-primary">Add tag</button>
            <ul>
              {(data.tags || []).map((tag) => (
                <li key={tag.slug} className="support-tag-row">
                  <span>{tag.label} ({tag.slug}) · {tag.discipline || 'no discipline'}{tag.review ? ' · needs a review' : ''}</span>
                  <button type="button" className="rf-btn rf-btn-danger" onClick={() => removeTag(tag)}>Delete</button>
                </li>
              ))}
            </ul>
          </form>

          <section className="support-form">
            <h2>Waiting for approval</h2>
            {(data.suggestions || []).length === 0 && <p className="support-meta">Nothing is waiting. A term appears after it shows up on two tickets.</p>}
            <ul>
              {(data.suggestions || []).map((row) => (
                <li key={row.term}>
                  {row.term} · {row.count} tickets
                  {' '}
                  <button type="button" className="rf-btn rf-btn-secondary" onClick={() => decide(row.term, { label: row.term, discipline: 'electrical' })}>Approve as tag</button>
                  {' '}
                  <button type="button" className="rf-btn rf-btn-outline" onClick={() => decide(row.term, { action: 'ignore' })}>Ignore</button>
                </li>
              ))}
            </ul>
          </section>

          <form className="support-form rf-card rf-card-body" onSubmit={saveRouting}>
            <h2>Who is emailed</h2>
            <p className="support-meta">Routing stays off until TICKETS_ROUTING_ENABLED is turned on. The strongest match is assigned. The others are copied on the same email. If Brian, Sam or Tom is on leave, the ticket goes to Martin.</p>
            {(data.disciplines || []).map((item) => (
              <div key={item.id} className="support-filters">
                <span>{item.label}</span>
                <input className="rf-input" name={`name-${item.id}`} defaultValue={data.routing?.people?.[item.id]?.name || ''} />
                <input className="rf-input" name={`email-${item.id}`} type="email" placeholder="name@rapidfusion.co.uk" defaultValue={data.routing?.people?.[item.id]?.email || ''} />
              </div>
            ))}
            <button type="submit" className="rf-btn rf-btn-primary">Save people</button>
          </form>

          <section className="support-form">
            <h2>FAQ drafts</h2>
            <ul>
              {faqs.filter((item) => item.question).map((item) => (
                <li key={item.id}>
                  {item.status === 'published' ? 'Published' : 'Draft'}: {item.question}
                  {item.status !== 'published' && (
                    <>
                      {' '}
                      <button type="button" className="rf-btn rf-btn-secondary" onClick={() => publish(item.id, 'published')}>Publish</button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </section>
  );
}
